// Article engagement tracking. Fires dataLayer events for how far and how long a
// reader engages with an article, plus internal-link and demo-CTA clicks from
// within the article body. Feeds GTM -> GA4 so the content strategy can be judged
// on engagement + pipeline influence, not just pageviews.
//
// Events pushed to dataLayer (wire matching GA4 tags in GTM):
//   article_scroll   { article_slug, percent }   fired once each at 25/50/75/100%
//   article_read     { article_slug, seconds }   fired at 30s and 60s of active time
//   article_link_click { article_slug, href, kind: 'internal'|'cta'|'external' }
//
// Client-only, best-effort: it never throws and no-ops during SSR or if the
// dataLayer isn't present (e.g. consent not granted / GTM blocked).
export const useArticleEngagement = (slug) => {
  if (process.server) return () => {}

  const track = useTrack() // first-party collector (not ad-blockable)

  // Take ownership of scroll reporting for this page. The analytics plugin
  // tracks generic `scroll` depth site-wide; articles report the same depth with
  // the slug attached, so without this both fire and depth is double-counted.
  const { $analyticsClaimScroll } = useNuxtApp()
  if (typeof $analyticsClaimScroll === 'function') $analyticsClaimScroll()

  const push = (event, extra = {}) => {
    // GTM/GA4 path (browser tag) + first-party collector, so engagement is
    // captured even for visitors who block third-party analytics.
    if (window.dataLayer) window.dataLayer.push({ event, article_slug: slug, ...extra })
    track(event, { article_slug: slug, ...extra })
  }

  // --- scroll depth (25/50/75/100), each fired once ---
  const thresholds = [25, 50, 75, 100]
  const fired = new Set()
  const onScroll = () => {
    const doc = document.documentElement
    const scrollable = doc.scrollHeight - window.innerHeight
    if (scrollable <= 0) return
    const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100))
    for (const t of thresholds) {
      if (pct >= t && !fired.has(t)) {
        fired.add(t)
        push('article_scroll', { percent: t })
      }
    }
  }

  // --- active read time (only counts while the tab is visible) ---
  let activeMs = 0
  let lastTick = Date.now()
  const readMarks = [30, 60]
  const readFired = new Set()
  const tick = () => {
    const now = Date.now()
    if (document.visibilityState === 'visible') activeMs += now - lastTick
    lastTick = now
    const secs = Math.round(activeMs / 1000)
    for (const m of readMarks) {
      if (secs >= m && !readFired.has(m)) {
        readFired.add(m)
        push('article_read', { seconds: m })
      }
    }
  }
  const readTimer = setInterval(tick, 5000)

  // --- link clicks within the article body ---
  // INTERNAL links only. The analytics plugin binds a document-level click
  // handler that reports off-domain links as outbound_click and document links
  // as file_download; emitting here as well meant one click on a legislation
  // citation produced two collector events and inflated article outbound
  // traffic against the rest of the site.
  const onClick = (e) => {
    const a = e.target?.closest?.('a[href]')
    if (!a) return
    const href = a.getAttribute('href') || ''
    if (href.startsWith('#')) return
    if (/^(mailto|tel):/i.test(href)) return // the plugin owns contact links
    // Resolve properly rather than substring-matching 'elker.com': a link to
    // https://cdn.elker.com/report.pdf passed that test AND matched the
    // plugin's download rule, so one click produced two collector events.
    let url = null
    try {
      url = new URL(href, window.location.origin)
    } catch {
      return
    }
    const isSameHost = url.host === window.location.host
    const isDownload = /\.(pdf|docx?|xlsx?|pptx?|csv|zip|rar|7z|gz|tar|mp3|mp4|mov|wav|avi|dmg|pkg|exe)(\?|#|$)/i.test(url.pathname)
    // The plugin owns off-domain links and downloads, wherever they point.
    if (!isSameHost || isDownload) return
    const kind =
      href.startsWith('/book-demo') || href.includes('book-demo') || /demo/i.test(a.textContent || '')
        ? 'cta'
        : 'internal'
    push('article_link_click', { href: href.slice(0, 200), kind })
  }

  window.addEventListener('scroll', onScroll, { passive: true })
  onScroll() // capture initial position (short articles fully visible on load)
  const body = document.getElementById('content') || document
  body.addEventListener('click', onClick)

  // teardown, returned so the page can call it onBeforeUnmount
  return () => {
    window.removeEventListener('scroll', onScroll)
    body.removeEventListener('click', onClick)
    clearInterval(readTimer)
  }
}
